import { ListItemButton, Typography } from '@mui/material';
import { useState } from 'react';
import { getClassSectionStudent, getSpecificClassSection } from 'apis/main';
import MuiAutocomplete from 'components/MuiAutocomplete';
import useClassSection from 'store/section/useClassSection';
import useStudent from 'store/student/useStudent';
import { getStudentList } from 'apis/admin';
import { Dot } from 'styles/dot';
import usePatient from 'store/patient/usePatient';

interface IOption {
  user_id: number;
  student_name: string;
  student_no: string;
  nursing_record_exists?: number;
  other_record_exists?: number;
}

const StudentList = ({ user_id }: Pick<IOption, 'user_id'>) => {
  const { onSelectedStudent } = useStudent();
  const { class_section_id } = useClassSection();
  const { patient } = usePatient();
  const [studentsAvailable, setStudentsAvailable] = useState<boolean>(true);
  const patientId = patient?.patient_id;

  const optionLabel = ({ student_name, student_no }: IOption) => {
    const name = student_name || '';
    const no = student_no || '';
    return `${name} ${no}`.trim();
  };

  const Option = ({ student_name, student_no, ...props }: IOption) => (
    <ListItemButton {...props} sx={{ gap: 0.5 }}>
      <Typography variant="subtitle2">{student_name || '-'}</Typography>
      <Typography variant="caption" color={'#000000B2'}>
        {student_no || '-'}
      </Typography>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {props.nursing_record_exists === 1 && <Dot $bg="#14855B" />}
        {props.other_record_exists === 1 && <Dot $bg="#9C57C7" />}
      </div>
    </ListItemButton>
  );

  return (
    <MuiAutocomplete
      variant="standard"
      valueKey="student_no"
      listKey="student_defaults"
      placeholder="Оюутнуудыг хайх"
      noOptionsText={
        studentsAvailable
          ? 'Анги бүлгийг сонгоно уу'
          : 'Энэ бүлэгт оюутан байхгүй байна.'
      }
      getOptionLabel={optionLabel}
      renderOption={(props, option) => {
        if (!user_id || class_section_id === null) {
          return null;
        }
        return <Option {...props} {...option} />;
      }}
      onChange={e => {
        if (e && onSelectedStudent) {
          onSelectedStudent(e);
        }
      }}
      getApi={async request => {
        try {
          // 검색어가 있을 경우 검색 API 호출
          if (request?.keyword) {
            const response = await getStudentList({
              user_id,
              searchType: 2,
              ...request,
            });

            const students = response?.data?.student_defaults || [];
            setStudentsAvailable(students.length > 0);

            return {
              ...response,
              data: {
                ...response?.data,
                student_defaults: students,
              },
            };
          }

          // 기존 로직 유지
          if (class_section_id == null) {
            return Promise.resolve({
              data: { rc: 0, student_defaults: [] },
              status: 200,
              statusText: 'OK',
              headers: {},
              config: {},
            });
          }

          let response;
          if (class_section_id === -1) {
            // 전체 학생 조회
            response = await getClassSectionStudent(user_id, 0, patientId);
          } else {
            // 특정 분반 학생 조회
            response = await getSpecificClassSection(
              user_id,
              1,
              class_section_id,
              patientId
            );
          }

          const students = response?.data?.student_defaults || [];
          setStudentsAvailable(students.length > 0);

          return {
            ...response,
            data: {
              ...response?.data,
              student_defaults: students,
            },
          };
        } catch (error) {
          console.error('Failed to fetch students:', error);
          setStudentsAvailable(false);

          return Promise.resolve({
            data: { rc: -1, student_defaults: [] },
            status: 200,
            statusText: 'OK',
            headers: {},
            config: {},
          });
        }
      }}
    />
  );
};

export default StudentList;
